import axios, { AxiosResponse } from 'axios'
import { getAppBaseUrl } from './api'

const baseURL = getAppBaseUrl()

export const getWhiteLabelSites = (): Promise<AxiosResponse> => {
    return axios.get(`${baseURL}/whitelabel/sites`)
}

export const getWhiteLabelSite = (siteId?: string, options: {
    personaAgentKey?: string
    includeRuntimePreview?: boolean
} = {}): Promise<AxiosResponse> => {
    const params = new URLSearchParams()
    if (siteId) params.set('site_id', siteId)
    if (options.personaAgentKey) params.set('persona_agent_key', options.personaAgentKey)
    if (options.includeRuntimePreview === true) params.set('include_runtime_preview', 'true')
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return axios.get(`${baseURL}/whitelabel/site${suffix}`)
}

export const saveWhiteLabelSite = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site`, data)
}

export const prepareWhiteLabelAssetUpload = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/v2/assets/presign`, data)
}

export const finalizeWhiteLabelAssetUpload = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/v2/assets/finalize`, data)
}

export const prepareWhiteLabelSite = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/launch-setup`, data)
}

export const cloneWhiteLabelSite = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/clone`, data)
}

export const activateWhiteLabelSite = (site_id: string): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/activate`, { site_id })
}

export const deactivateWhiteLabelSite = (site_id: string): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/deactivate`, { site_id })
}

export const saveWhiteLabelPersona = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/persona`, data)
}

export const saveWhiteLabelPersonaMedia = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/persona/media`, data)
}

export const saveWhiteLabelCoupon = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/coupon`, data)
}

export const removeWhiteLabelCoupon = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/coupon/remove`, data)
}

export const saveWhiteLabelTesterAccess = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/tester-access`, data)
}

export const removeWhiteLabelTesterAccess = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/tester-access/remove`, data)
}

export const removeWhiteLabelPersona = (site_id: string, agent_key: string): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/persona/remove`, { site_id, agent_key })
}

export const getWhiteLabelCreditWallet = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/credit-wallet`, data)
}

export const saveWhiteLabelCreditAdjustment = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/credit-adjustment`, data)
}

export const getWhiteLabelCreditBundlePurchases = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/credit-bundle-purchases`, data)
}

export const reconcileWhiteLabelCreditBundlePurchase = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/credit-bundle-purchase/reconcile`, data)
}

export const getWhiteLabelSubscriptionState = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/subscription-state`, data)
}

export const runWhiteLabelWeeklyCreditGrant = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/weekly-credit-grant`, data)
}

export const runWhiteLabelSubscriptionMonthlyCreditGrant = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/subscription-monthly-credit-grant`, data)
}

export const getWhiteLabelPartnerUsers = (): Promise<AxiosResponse> => {
    return axios.get(`${baseURL}/whitelabel/partner-users`)
}

export const saveWhiteLabelPartnerUser = (data: object): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/partner-user`, data)
}

export const deleteWhiteLabelPartnerUser = (_id: string): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/partner-user/delete`, { _id })
}

export const updateWhiteLabelPartnerUserStatus = (_id: string, is_active: boolean): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/partner-user/status`, { _id, is_active })
}

export const resetWhiteLabelPartnerUserPassword = (_id: string, password: string): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/partner-user/reset-password`, { _id, password })
}

export const assignAllWhiteLabelPersonas = (site_id: string, agent_keys?: Array<string>): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/persona/assign-all`, { site_id, agent_keys })
}

export const removeAllWhiteLabelPersonas = (site_id: string, agent_keys?: Array<string>): Promise<AxiosResponse> => {
    return axios.post(`${baseURL}/whitelabel/site/persona/remove-all`, { site_id, agent_keys })
}

export default {
    activateWhiteLabelSite,
    cloneWhiteLabelSite,
    deactivateWhiteLabelSite,
    assignAllWhiteLabelPersonas,
    getWhiteLabelSites,
    getWhiteLabelPartnerUsers,
    getWhiteLabelSite,
    getWhiteLabelCreditBundlePurchases,
    getWhiteLabelCreditWallet,
    getWhiteLabelSubscriptionState,
    reconcileWhiteLabelCreditBundlePurchase,
    removeWhiteLabelCoupon,
    removeAllWhiteLabelPersonas,
    removeWhiteLabelTesterAccess,
    runWhiteLabelSubscriptionMonthlyCreditGrant,
    runWhiteLabelWeeklyCreditGrant,
    prepareWhiteLabelSite,
    prepareWhiteLabelAssetUpload,
    saveWhiteLabelSite,
    saveWhiteLabelPartnerUser,
    saveWhiteLabelCreditAdjustment,
    saveWhiteLabelCoupon,
    saveWhiteLabelPersona,
    saveWhiteLabelPersonaMedia,
    saveWhiteLabelTesterAccess,
    removeWhiteLabelPersona,
    deleteWhiteLabelPartnerUser,
    updateWhiteLabelPartnerUserStatus,
    resetWhiteLabelPartnerUserPassword
}
